import React from 'react';

interface RackUnitProps {
  minHeight?: number; // Minimum height in pixels
  unitHeight?: number; // Display height in rack units for visual reference
  label: string;
  unitNumber?: number;
  children: React.ReactNode;
  accentColor?: string;
}

/**
 * Hardware Rack Unit Component
 * 
 * Represents a physical rack-mounted unit with metal casing, screw holes, and labels.
 * Wrapped in React.memo to prevent unnecessary re-renders when parent state changes.
 */
const RackUnit: React.FC<RackUnitProps> = React.memo(({
  minHeight = 400,
  unitHeight = 3,
  label,
  unitNumber,
  children,
  accentColor = '#8b5cf6'
}) => {
  const visualHeight = unitHeight * 60; // Larger visual units for better scaling

  return (
    <div
      style={{
        position: 'relative',
        minHeight: `${minHeight}px`,
        marginBottom: '12px', // Rack spacing between units
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      {/* Rack Mount Rails - Left */}
      <div
        style={{
          position: 'absolute',
          left: '-24px',
          top: '0',
          bottom: '0',
          width: '20px',
          background: 'linear-gradient(to right, #1a1a1a 0%, #2d2d2d 100%)',
          borderLeft: '3px solid #0a0a0a',
          borderRight: '2px solid #404040',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '8px 0',
          gap: '40px'
        }}
      >
        {/* Screw holes */}
        {Array.from({ length: Math.max(3, Math.floor(minHeight / 120)) }).map((_, i) => (
          <div
            key={i}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, #4a4a4a 0%, #1a1a1a 70%)',
              border: '1px solid #0a0a0a',
              boxShadow: 'inset 0 0 2px rgba(0,0,0,0.8), 0 1px 1px rgba(255,255,255,0.1)'
            }}
          />
        ))}
      </div>

      {/* Rack Mount Rails - Right */}
      <div
        style={{
          position: 'absolute',
          right: '-24px',
          top: '0',
          bottom: '0',
          width: '20px',
          background: 'linear-gradient(to left, #1a1a1a 0%, #2d2d2d 100%)',
          borderRight: '3px solid #0a0a0a',
          borderLeft: '2px solid #404040',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '8px 0',
          gap: '40px'
        }}
      >
        {/* Screw holes */}
        {Array.from({ length: Math.max(3, Math.floor(minHeight / 120)) }).map((_, i) => (
          <div
            key={i}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, #4a4a4a 0%, #1a1a1a 70%)',
              border: '1px solid #0a0a0a',
              boxShadow: 'inset 0 0 2px rgba(0,0,0,0.8), 0 1px 1px rgba(255,255,255,0.1)'
            }}
          />
        ))}
      </div>

      {/* Metal Casing — opaque gradient background instead of backdrop-filter for GPU performance */}
      <div
        style={{
          flex: 1,
          background: `
            linear-gradient(135deg, 
              rgba(30, 30, 30, 0.94) 0%, 
              rgba(22, 22, 22, 0.94) 25%, 
              rgba(28, 28, 28, 0.94) 50%, 
              rgba(22, 22, 22, 0.94) 75%, 
              rgba(30, 30, 30, 0.94) 100%
            )
          `,
          border: '2px solid rgba(10, 10, 10, 0.5)',
          borderRadius: '4px',
          boxShadow: `
            inset 0 1px 3px rgba(255,255,255,0.1),
            inset 0 -1px 3px rgba(0,0,0,0.3),
            0 4px 8px rgba(0,0,0,0.4),
            0 0 0 1px rgba(64, 64, 64, 0.5)
          `,
          position: 'relative',
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}
      >
        {/* Top Screw Row */}
        <div
          style={{
            height: '24px',
            borderBottom: '2px solid #0a0a0a',
            background: 'linear-gradient(to bottom, #1a1a1a 0%, #2d2d2d 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 12px',
            position: 'relative'
          }}
        >
          {/* Unit Label */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '11px',
              fontFamily: 'monospace',
              color: accentColor,
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            <div
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: accentColor,
                boxShadow: `0 0 6px ${accentColor}`,
                animation: 'pulse 2s ease-in-out infinite'
              }}
            />
            {label}
          </div>

          {/* Unit Number Badge */}
          {unitNumber !== undefined && (
            <div
              style={{
                width: '20px',
                height: '16px',
                background: '#0a0a0a',
                border: `1px solid ${accentColor}`,
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontFamily: 'monospace',
                color: accentColor,
                fontWeight: '700'
              }}
            >
              {unitNumber}
            </div>
          )}

          {/* Corner Screws */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {[0, 1].map(i => (
              <div
                key={i}
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle at 30% 30%, #555 0%, #222 50%, #000 100%)',
                  border: '1px solid #0a0a0a',
                  boxShadow: `
                    inset 0 0 3px rgba(0,0,0,0.9),
                    inset -1px -1px 2px rgba(255,255,255,0.1),
                    0 1px 2px rgba(0,0,0,0.8)
                  `
                }}
              />
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div
          style={{
            flex: 1,
            padding: '16px',
            overflow: 'visible',
            background: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          {children}
        </div>

        {/* Bottom Border with Vents */}
        <div
          style={{
            height: '16px',
            borderTop: '2px solid #0a0a0a',
            background: `
              repeating-linear-gradient(
                90deg,
                #1a1a1a 0px,
                #1a1a1a 8px,
                #0a0a0a 8px,
                #0a0a0a 10px
              )
            `,
            opacity: 0.6
          }}
        />
      </div>
    </div>
  );
});

RackUnit.displayName = 'RackUnit';

export default RackUnit;
export type { RackUnitProps };
